import React, { RefObject } from "react";
import styled from "styled-components";
import { Moment } from "moment";
import useResizeObserver from "use-resize-observer/polyfilled";
import {
  getTotalColumns,
  useGetBounds,
} from "@sonder/ui/source/styled/components/ScheduleLayer/helpers";
import {
  ScheduleBoundingParent,
  ScheduleBounds,
} from "@sonder/ui/source/styled/fragments/ScheduleLayer";
import { useStore } from "schedule/store";

interface StyledBoundsProps {
  disabled?: boolean;
}

const StyledBounds = styled(ScheduleBounds)<StyledBoundsProps>`
  background: rgba(0, 0, 0, 0.1);
  opacity: ${props => (props.disabled ? "0" : "1")};
`;

export interface ScheduleBoundingLayerProps {
  disabled?: boolean;
  endAt: Moment;
  maxEndAt: Moment;
  maxStartAt: Moment;
  startAt: Moment;
  taskId: string;
}

export const ScheduleBoundingLayer = ({
  disabled,
  endAt: unboundedEndAt,
  maxEndAt,
  maxStartAt,
  startAt: unboundedStartAt,
  taskId,
}: ScheduleBoundingLayerProps) => {
  // Prevent Scheduling outside of Bounds.
  const startAt = unboundedStartAt.isSameOrBefore(maxStartAt)
    ? maxStartAt
    : unboundedStartAt;
  const endAt = unboundedEndAt.isSameOrAfter(maxEndAt)
    ? maxEndAt
    : unboundedEndAt;
  const {
    state: { hoveringTaskId, isDisabled, isSelecting, modifyingTaskId },
  } = useStore();
  const isBoundsDisabled =
    disabled ||
    isDisabled ||
    isSelecting ||
    (modifyingTaskId !== taskId && hoveringTaskId !== taskId);

  const cols = getTotalColumns({ endAt: maxEndAt, startAt: maxStartAt });
  const { ref, width: parentLayerWidth = 0 } = useResizeObserver();
  const totalMaxTime = maxEndAt.diff(maxStartAt);
  const { boundsStartAtX, boundsWidth } = useGetBounds({
    endAt,
    startAt,
    cols,
    maxStartAt,
    maxEndAt,
    parentLayerWidth,
    totalMaxTime,
  });

  return (
    <ScheduleBoundingParent ref={ref as RefObject<HTMLDivElement>}>
      <StyledBounds
        disabled={isBoundsDisabled}
        style={{
          transform: `translate3d(${boundsStartAtX}px, 0, 0)`,
          width: `${boundsWidth}px`,
        }}
      />
    </ScheduleBoundingParent>
  );
};
